import * as _ from "lodash"
import {
    IObjectTreeContentProviderSearchRequest,
    IObjectTreeDataManager,
    IObjectTreeDataManagerOperationEvent,
    IObjectTreeDataManagerOptions,
    IObjectTreeDataManagerSchemaProperty, IObjectTreeFolder,
    ITreeObject, ITreeSearchFolder
} from "../PA.Core/PA.Core.IF.ObjectTree";
import { ISearch, ISearchProvider } from "./PA.UIM.Core.IF.Search";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { ESRecordMapper } from "../PA.Core/lib/ESRecordMapper";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { BehaviorSubject } from "rxjs";

export class SearchObjectTreeDataManager
    implements IObjectTreeDataManager
{
    private oSearch: ISearch
    private searchProvider: ISearchProvider
    private folderFieldName: string
    private nameFieldName: string
    private stdCnd: any
    private mapperId: string
    private operationEvent: BehaviorSubject<IObjectTreeDataManagerOperationEvent>
    private dmOptions?: IObjectTreeDataManagerOptions
    private quickSearchFields: string[]

    constructor(search: ISearch,
                mapperId: string,
                wbTreeFolderFieldName: string,
                nameFieldName: string,
                stdCnd?: any,
                options?: IObjectTreeDataManagerOptions) {
        //--
        this.oSearch = search
        this.mapperId = mapperId
        this.folderFieldName = wbTreeFolderFieldName
        this.nameFieldName = nameFieldName
        this.stdCnd = stdCnd
        this.dmOptions = options || {}
        if(!('mapper' in this.dmOptions))
            this.dmOptions.mapper = {}
        if(!(this.mapperId in this.dmOptions.mapper))
            this.dmOptions.mapper[this.mapperId] = {}

        let searchProfile = _.first(_.filter(this.oSearch.searchProfiles, (a) => a.profileId == this.oSearch.searchProfileIdDefault))
        if(_.isEmpty(searchProfile))
            searchProfile = _.first(this.oSearch.searchProfiles)
        if(searchProfile){
            this.searchProvider = searchProfile.profileSearchProvider
            if (_.isEmpty(this.dmOptions.displayProperties)) {
                let propIds = <string[]>_.filter(searchProfile.profileDisplayFields, (a) => _.isString(a));
                this.dmOptions.displayProperties = _.filter(propIds, (a) => ( (a != this.searchProvider.keyFields.name) && (a != this.searchProvider.keyFields.id_ui) ))
            }
            this.quickSearchFields = [this.searchProvider.keyFields.name, this.searchProvider.keyFields.id_ui]
            _.forEach(searchProfile.profileFilter, (pf) => {
                if(pf.bQuickSearch && !_.isEmpty(pf.searchProviderFieldId)){
                    if(!_.includes(this.quickSearchFields, pf.searchProviderFieldId))
                        this.quickSearchFields.push(pf.searchProviderFieldId)
                }
            })
        }
        this.operationEvent = new BehaviorSubject<IObjectTreeDataManagerOperationEvent>(undefined)
    }

    get propertySchema(): { [p: string]: IObjectTreeDataManagerSchemaProperty } {
        return this.searchProvider?.searchProviderFields || {}
    }

    get options(): IObjectTreeDataManagerOptions { return this.dmOptions }

    get OperationEvent(): BehaviorSubject<IObjectTreeDataManagerOperationEvent> { return this.operationEvent }

    async getObjects(wbTreeFolderId: number, options?: IObjectTreeContentProviderSearchRequest): Promise<ITreeObject[]> {
        return this.getObjectsFromFolders([wbTreeFolderId], options)
    }


    async moveObjectsToFolder(mapperName: string, arrWbIds: number[], wbTreeFolderId: number): Promise<void> {
        const self = this

        let dbContext  = new PAdbContext()
        let mapper = <ESRecordMapper> dbContext.getMapper(this.mapperId)
        if(_.isEmpty(mapper))
            return
        let cnd: any = {where: {}}
        cnd.where[mapper.idAttribute] = {inq: arrWbIds}
        if(mapper.bDeleteIndicatorExists)
            cnd.where[mapper.DeletedIndicatorFieldName] = {neq: '1'}

        let rows = await dbContext.findAll(self.mapperId, cnd)

        // Change Folder specification
        _.forEach(rows, (aRow) => {
            aRow[self.folderFieldName] = wbTreeFolderId
        })
        await dbContext.Save(self.mapperId, rows)

        self.operationEvent.next({ObjectTreeDataManager: self, Operation: "MoveObjectsToFolder", wbTreeFolderId: wbTreeFolderId})
    }

    async renameObject(mapperName: string, wbId: number, sName: string): Promise<void> {
        const self = this

        if(mapperName!= this.mapperId)
            return

        let dbContext  = new PAdbContext()
        let mapper = <ESRecordMapper> dbContext.getMapper(this.mapperId)
        if(_.isEmpty(mapper))
            return
        if(_.isEmpty(this.nameFieldName))
            return

        if(!(wbId > 0))
            return

        let row = <ESRecord | any> await dbContext.find(self.mapperId, wbId)
        if(_.isEmpty(row))
            return

        row[this.nameFieldName] = sName
        await dbContext.Save(self.mapperId, row)

        self.operationEvent.next({ObjectTreeDataManager: self, Operation: "RenameObject"})
    }


    // Recursive search
    async search(folder: IObjectTreeFolder, options?: IObjectTreeContentProviderSearchRequest): Promise<ITreeSearchFolder> {
        const self = this

        if(_.isEmpty(folder))
            return undefined

        const tree = folder.Tree
        const arrWbFolderIds: number[] = []
        function addAllSubFolders(f: IObjectTreeFolder) {
            if (!f)
                return;
            arrWbFolderIds.push(folder.wbId);
            _.forEach(f.SubFolders, (a) => {
                addAllSubFolders(a)
            })
        }

        // Search everything if at the root (wbId = 0),
        //   otherwise, limit to self and all child folders
        if(folder.wbId > 0)
            addAllSubFolders(folder)

        const treeObjs = await self.getObjectsFromFolders(arrWbFolderIds, options)

        // Organize into dictionary, add Root Node
        const dctSF: {[id: number]: ITreeSearchFolder} = {
            [folder.wbId]: {
                TreeFolderName: (folder.wbId == 0)?'.': folder.Name,
                TreeFolder: folder,
                Parent: undefined,
                TreeObjects: [],
                TreeObjectCount: 0,
                SubFolders: []
            }
        }

        _.forEach(treeObjs, (to) => {
            const treeFolderId: number = +(to.properties[self.folderFieldName] || 0)

            if(!(treeFolderId in dctSF))
                dctSF[treeFolderId] =  {
                    TreeFolderName: "",
                    TreeFolder: undefined,
                    Parent: undefined,
                    TreeObjects: [],
                    TreeObjectCount: 0,
                    SubFolders: []
                }

            const sf = dctSF[treeFolderId]
            sf.TreeObjects.push(to)
        })

        // Setup SubFolders../ Parents/ Counts using Tree
        function setupSearchFolder(tf: IObjectTreeFolder,
                                   parent: ITreeSearchFolder): ITreeSearchFolder
        {
            // Find in dct
            let wbId = tf.wbId
            if(!(wbId in dctSF))
                dctSF[wbId] =  {
                    TreeFolderName: tf.Name,
                    TreeFolder: tf,
                    Parent: undefined,
                    TreeObjects: [],
                    TreeObjectCount: 0,
                    SubFolders: []
                }

            const searchFldr = dctSF[wbId]
            searchFldr.TreeFolderName = (tf.wbId == 0)?'.': tf.Name
            searchFldr.TreeFolder = tf
            searchFldr.Parent = parent
            searchFldr.TreeObjectCount = searchFldr.TreeObjects.length

            // Add SubFolders
            _.forEach(tf.SubFolders, (subF) => {
                const searchSubFldr = setupSearchFolder(subF, searchFldr)
                // Prune to only include folders that have children
                if(searchSubFldr.TreeObjectCount > 0) {
                    searchFldr.SubFolders.push(searchSubFldr)
                    searchFldr.TreeObjectCount += searchSubFldr.TreeObjectCount
                }
            })
            return searchFldr
        }


        const root = dctSF[folder.wbId]
        setupSearchFolder(folder, undefined)
        // Find orphaned search items.. not placed.
        // (Parent not set, and not root)

        // Add Orphans to Root
        _.forEach(dctSF, (sf: ITreeSearchFolder, key) => {
            if((sf == root) ||
               (sf.TreeObjects.length == 0) ||
               (sf.Parent != undefined))
            return

            // Orphaned Node, not in Tree
            sf.Parent = root
            sf.TreeFolderName = `Orphaned, Unknown Folder ${key}`
            root.SubFolders.push(sf)
        })

        return root
    }

    private async getObjectsFromFolders(arrWbTreeFolderIds: number[], options?: IObjectTreeContentProviderSearchRequest): Promise<ITreeObject[]> {
        const self = this
        if(_.isEmpty(self.searchProvider))
            return []
        const opts = options || {}
        const filterCnd = opts.where || {}
        let searchCnd = _.clone(self.stdCnd)
        _.defaults(searchCnd, filterCnd)

        if(!_.isEmpty(arrWbTreeFolderIds))
            searchCnd[self.folderFieldName] = {inq: arrWbTreeFolderIds }

        // Add Quick Search
        if(!_.isEmpty(opts.quickSearch) && !_.isEmpty(self.quickSearchFields)){
            const sSearchString = `%${opts.quickSearch}%`

            const cndQS = {or: _.map(self.quickSearchFields,
                    (searchField) => {return {[searchField]: {ilike: sSearchString}}})
            }

            if(_.isEmpty(searchCnd))
                searchCnd = cndQS
            else
                searchCnd = {and: [searchCnd, cndQS]}
        }

        const results = await self.searchProvider.search({
            limit: opts.limit || 0,
            order: opts.order || [],
            where: searchCnd
        })

        // Convert to TreeObjects
        const dctId: {[id:number]: number} = {}

        const rv: ITreeObject[] = []
        _.forEach(results, (aRow) => {
            let wbId = aRow[self.searchProvider.keyFields.id]
            let id_ui = aRow[self.searchProvider.keyFields.id_ui]
            let name = aRow[self.searchProvider.keyFields.name]

            // Check Distinct
            if(wbId in dctId)
                return
            dctId[wbId] = wbId

            rv.push({
                wb_id: wbId,
                mapperName: this.mapperId,
                id_ui: id_ui,
                name: name,
                properties : aRow
            })

        })

        return rv
    }



}